import { pickLocalized } from '@yoyoclub/shared';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { DescriptionMarkdown } from '../../components/DescriptionMarkdown';
import FollowHostButton from '../../components/FollowHostButton';
import DetailActions from '../../components/site/DetailActions';
import Card from '../../components/ui/Card';
import ImageGallery from '../../components/ui/ImageGallery';
import { useAuth } from '../../hooks/useAuth';
import { localize } from '../../i18n/script';
import { courseApi, type CourseLesson, type CoursePaymentMethod } from '../../services/courseApi';
import { storeCreditApi } from '../../services/storeCreditApi';
import { getLocalizedTitle } from '../../utils/activityTransform';
import { trackBeginCheckout } from '../../utils/analytics';
import {
  forgetMemberCode,
  getRememberedMemberCode,
  promoFailureText,
} from '../../utils/memberCode';

// Mirror of the backend promo discount math (USD cents).
function calcPromoDiscount(
  type: 'FIXED_AMOUNT' | 'PERCENTAGE',
  value: number,
  subtotalCents: number
): number {
  if (type === 'FIXED_AMOUNT') return Math.min(value, subtotalCents);
  if (type === 'PERCENTAGE') return Math.floor((subtotalCents * value) / 100);
  return 0;
}

interface CourseInfo {
  id: string;
  slug: string;
  titleEN: string | null;
  titleCN: string | null;
  descriptionCN: string | null;
  descriptionEN: string | null;
  price: number;
  priceCny: number | null;
  category?: string;
  ageMin?: number;
  ageMax?: number;
  // The exact currency/amount WeChat checkout will use (resolved by backend).
  wechat: { currency: 'usd' | 'cny'; amount: number };
  // Full gallery (reuses the activity ImageGallery component).
  images: Array<{ imageUrl: string; order: number; isCover: boolean }>;
  host?: {
    id: string;
    displayName: string;
    avatarUrl?: string | null;
    bioZh?: string | null;
    bioEn?: string | null;
  };
  lessons: CourseLesson[];
  // Non-live (not APPROVED/ACTIVE) means an unlisted preview reachable only by
  // direct link; show a preview banner.
  isLive: boolean;
  // True only when live AND every lesson is ready+approved. A live course mid-
  // update (a new/processing lesson) is live but not yet purchasable.
  purchasable: boolean;
}

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatCny(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}
function formatMoney(currency: 'usd' | 'cny', cents: number): string {
  return currency === 'cny' ? formatCny(cents) : formatUsd(cents);
}
// Video-style runtime (m:ss, or h:mm:ss past an hour). Null until the video has
// finished processing and the provider reports a duration.
function formatDuration(sec?: number | null): string | null {
  if (!sec || sec <= 0) return null;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// Recorded-course detail + per-lesson purchase. Buyers pick one or more lessons
// (or all) and pay once. Payment method is a mutually exclusive choice (card vs
// WeChat Pay) so a buyer never starts two charges at once.
export default function CourseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['course', 'common']);
  const { isAuthenticated, getToken } = useAuth();

  const [course, setCourse] = useState<CourseInfo | null>(null);
  const [ownedVideoIds, setOwnedVideoIds] = useState<Set<string>>(new Set());
  const [ownsWholeCourse, setOwnsWholeCourse] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<CoursePaymentMethod>('card');
  const [agreed, setAgreed] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Discounts (USD only): store-credit balance + an applied promo code.
  const [creditBalance, setCreditBalance] = useState(0);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    discountType: 'FIXED_AMOUNT' | 'PERCENTAGE';
    discountValue: number;
  } | null>(null);
  // A member's new user promo code, stacked on at most one platform code (member rewards PRD §3.2)
  const [appliedInvite, setAppliedInvite] = useState<{
    code: string;
    discountValue: number;
  } | null>(null);
  // Bumped per promo validation request; a response for a stale request (the
  // course changed, or a newer Apply fired) is ignored.
  const promoRequestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Clear per-course state so a prior course's ownership/selection can never
        // bleed into this one (e.g. navigating between courses while mounted, or
        // an ownership fetch that later fails for a logged-out user).
        setSelected(new Set());
        setOwnsWholeCourse(false);
        setOwnedVideoIds(new Set());
        setAgreed(false);
        setMethod('card');
        setNeedsLogin(false);
        setCreditBalance(0);
        setPromoCode('');
        setPromoError(null);
        setAppliedPromo(null);
        setAppliedInvite(null);
        // Invalidate any promo validation still in flight for a prior course.
        promoRequestRef.current += 1;
        setPromoValidating(false);
        const data = await courseApi.getCourseBySlug(slug as string);
        if (cancelled) return;
        setCourse({
          id: data.id,
          slug: data.slug,
          titleEN: data.titleEN,
          titleCN: data.titleCN,
          descriptionCN: data.descriptionCN ?? null,
          descriptionEN: data.descriptionEN ?? null,
          price: data.price,
          priceCny: data.priceCny,
          category: data.category,
          ageMin: data.ageMin,
          ageMax: data.ageMax,
          wechat: data.wechat ?? { currency: 'usd', amount: data.price },
          images: (data.images ?? []).map((img, order) => ({ ...img, order })),
          host: data.host,
          lessons: data.lessons ?? [],
          isLive: data.status === 'APPROVED' || data.status === 'ACTIVE',
          purchasable: data.purchasable === true,
        });
        // If logged in, see which lessons the user already owns.
        if (isAuthenticated) {
          const token = await getToken();
          if (token && !cancelled) {
            const own = await courseApi.getCourseOwnership(data.id, token);
            if (!cancelled) {
              setOwnsWholeCourse(own.ownsWholeCourse);
              setOwnedVideoIds(new Set(own.ownedVideoIds));
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the course');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, isAuthenticated, getToken]);

  // Store-credit balance (USD) loads independently so a slow balance request can
  // never block the course page from rendering; it is non-critical to checkout.
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setCreditBalance(0);
      return;
    }
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const balance = await storeCreditApi.getBalance(token);
        if (!cancelled) setCreditBalance(balance);
      } catch {
        // Non-critical; checkout still works without showing a balance.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, isAuthenticated, getToken]);

  // Lessons the buyer does not yet own (the only ones that are selectable).
  const buyableLessons = useMemo(
    () => (course?.lessons ?? []).filter((l) => !ownedVideoIds.has(l.id)),
    [course, ownedVideoIds]
  );
  const ownsEverything =
    !!course && (ownsWholeCourse || (course.lessons.length > 0 && buyableLessons.length === 0));

  // Currency for the cart total follows the chosen payment method. WeChat uses
  // the backend-resolved currency (¥ when CNY pricing is on); card is USD.
  const cartCurrency: 'usd' | 'cny' =
    method === 'wechat_pay' ? (course?.wechat.currency ?? 'usd') : 'usd';
  const lessonAmount = (l: CourseLesson): number =>
    method === 'wechat_pay' ? l.wechat.amount : l.price;
  // Effective selection = only buyable (not-owned, still-listed) lessons that are
  // checked. Deriving this from buyableLessons prunes stale ids left in `selected`
  // after navigating to another course or after an ownership update, so the count,
  // total, and checkout cart never disagree.
  const selectedLessons = useMemo(
    () => buyableLessons.filter((l) => selected.has(l.id)),
    [buyableLessons, selected]
  );
  const selectedTotal = useMemo(
    () => selectedLessons.reduce((sum, l) => sum + lessonAmount(l), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLessons, method]
  );

  // Discounts apply only when the cart settles in USD (card, or WeChat with no
  // CNY price). A CNY WeChat cart pays the full price via Stripe.
  const usdContext = cartCurrency === 'usd';
  const promoDiscount =
    usdContext && appliedPromo
      ? calcPromoDiscount(appliedPromo.discountType, appliedPromo.discountValue, selectedTotal)
      : 0;
  // The new user promo code comes off what the platform code leaves (USD only).
  const inviteDiscount =
    usdContext && appliedInvite
      ? Math.min(appliedInvite.discountValue, selectedTotal - promoDiscount)
      : 0;
  const postPromoTotal = selectedTotal - promoDiscount - inviteDiscount;
  const creditApplied = usdContext ? Math.min(creditBalance, postPromoTotal) : 0;
  const toPay = postPromoTotal - creditApplied;

  const handleApplyPromoCode = async () => {
    if (!course || !promoCode.trim()) return;
    const requestId = ++promoRequestRef.current;
    const trimmed = promoCode.trim();
    try {
      setPromoValidating(true);
      setPromoError(null);
      const token = await getToken();
      if (!token) {
        setNeedsLogin(true);
        return;
      }
      const result = await courseApi.validatePromo(course.id, trimmed, token);
      // Ignore a response that is no longer the latest request (course changed or
      // a newer Apply superseded this one).
      if (requestId !== promoRequestRef.current) return;
      if (result.valid && result.discountType && result.discountValue != null) {
        // One platform code plus one new user promo code; a second of a kind replaces the first.
        if (result.isMemberCode) {
          setAppliedInvite({ code: trimmed.toUpperCase(), discountValue: result.discountValue });
        } else {
          setAppliedPromo({
            code: trimmed.toUpperCase(),
            discountType: result.discountType,
            discountValue: result.discountValue,
          });
        }
        setPromoCode('');
        setPromoError(null);
      } else {
        setPromoError(promoFailureText(t, result.reason, result.message || 'Invalid promo code'));
      }
    } catch (err) {
      if (requestId !== promoRequestRef.current) return;
      setPromoError(err instanceof Error ? err.message : 'Failed to validate code');
    } finally {
      if (requestId === promoRequestRef.current) setPromoValidating(false);
    }
  };
  const handleRemovePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError(null);
  };
  const handleRemoveInviteCode = () => {
    setAppliedInvite(null);
    setPromoError(null);
  };

  // A member's promo code from a shared link (member rewards PRD §5.2): applied
  // when it works for this buyer, dropped silently when it does not. USD only.
  const memberCodeTriedRef = useRef<string | null>(null);
  const courseId = course?.id;
  useEffect(() => {
    const remembered = getRememberedMemberCode();
    if (!remembered || !courseId || !isAuthenticated || !usdContext) return;
    if (appliedInvite || promoCode.trim()) return;
    const key = `${remembered}|${courseId}`;
    if (memberCodeTriedRef.current === key) return;
    memberCodeTriedRef.current = key;
    const requestId = ++promoRequestRef.current;
    void (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const result = await courseApi.validatePromo(courseId, remembered, token);
        if (requestId !== promoRequestRef.current) return;
        // Only a member's code is applied on its own (member rewards PRD §5.2).
        if (
          result.valid &&
          result.isMemberCode &&
          result.discountType &&
          result.discountValue != null
        ) {
          setAppliedInvite({ code: remembered, discountValue: result.discountValue });
        } else {
          // Not a member code, or it will not work for this account on any order.
          forgetMemberCode();
        }
      } catch {
        // Silent: the buyer can still type a code.
      }
    })();
  }, [courseId, isAuthenticated, usdContext, appliedInvite, promoCode, getToken]);

  const allSelected = buyableLessons.length > 0 && selectedLessons.length === buyableLessons.length;
  const toggleLesson = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    // Decide from the effective selection (allSelected), not raw `selected`, so a
    // stale id never makes the toggle and its checkbox state disagree.
    setSelected(allSelected ? new Set() : new Set(buyableLessons.map((l) => l.id)));
  };

  const handlePurchase = async () => {
    if (!course || !course.purchasable || selectedLessons.length === 0 || !agreed) return;
    // A promo is still validating: don't check out without the discount it may apply.
    if (promoValidating) return;
    // Guests are not redirected away: show a persistent prompt with a link to the
    // login/sign-up page (carrying a redirect back here) so they keep their cart.
    if (!isAuthenticated) {
      setNeedsLogin(true);
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const token = await getToken();
      if (!token) {
        setNeedsLogin(true);
        setSubmitting(false);
        return;
      }
      trackBeginCheckout({ type: 'course', title: getLocalizedTitle(course, i18n.language) });
      const videoIds = selectedLessons.map((l) => l.id);
      const result = await courseApi.createCheckout(
        course.id,
        method,
        token,
        videoIds,
        i18n.language,
        usdContext
          ? [appliedPromo?.code, appliedInvite?.code].filter((c): c is string => Boolean(c))
          : []
      );
      // Store credit / promo covered the whole total: no Stripe redirect, the
      // course is already granted, go straight to watching it.
      if (result.completed || !result.checkoutUrl) {
        // Bought. A Stripe checkout keeps the code until paid: once the course is
        // owned the code no longer applies and the next visit drops it.
        forgetMemberCode();
        navigate(`/courses/${course.slug}/watch`);
        return;
      }
      window.location.href = result.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-orange-500"></div>
          <p className="text-gray-600">{t('course:loading')}</p>
        </div>
      </div>
    );
  }
  if (!course) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="px-4 text-center text-red-600">{error || t('course:notFound')}</div>
      </div>
    );
  }

  // Course title in the active locale (Chinese when zh-*, else English), matching
  // how activities localize their title.
  const title = getLocalizedTitle(course, i18n.language);

  // Quick-info chips under the title (mirrors the activity page). Shown only when
  // the data is meaningful: a real age range (not the 0-99 "all ages" default) and
  // a category other than the OTHER fallback.
  const hasAgeRange = (course.ageMin ?? 0) > 0 || (course.ageMax ?? 99) < 99;
  const ageLabel =
    (course.ageMax ?? 99) >= 99 ? `${course.ageMin ?? 0}+` : `${course.ageMin}-${course.ageMax}`;
  const categoryLabel = (() => {
    if (!course.category || course.category === 'OTHER') return null;
    const key = `common:category.${course.category}`;
    const translated = t(key);
    return translated === key ? course.category : translated;
  })();
  const hostBio = course.host
    ? localize(course.host.bioZh, course.host.bioEn, i18n.language) || null
    : null;
  // Raw stored side; DescriptionMarkdown converts its rendered text (PRD §5.1).
  const description = pickLocalized(course.descriptionCN, course.descriptionEN, i18n.language);

  // With a CNY price, card (USD) and WeChat (CNY) are different currencies, so the
  // buyer must pick one up front. Without a CNY price both are the same USD amount,
  // so we skip the selector entirely and let Stripe offer card + WeChat together.
  const dualPricing = course.wechat.currency === 'cny';

  // Payment options, currency-first ("USD (Card)" / "CNY (WeChat Pay)"). The
  // WeChat currency is dynamic: a course with no CNY price charges WeChat in USD.
  const paymentOptions: { value: CoursePaymentMethod; label: string }[] = [
    {
      value: 'card',
      label: `${t('course:currency.usd')} (${t('course:method.card')})`,
    },
    {
      value: 'wechat_pay',
      label: `${t(`course:currency.${course.wechat.currency}`)} (${t('course:method.wechat')})`,
    },
  ];

  // Right-side purchase panel — mirrors the activity PriceCard (sticky Card),
  // but swaps the calendar/sessions for per-lesson selection. Rendered once for
  // mobile (above the description) and once for desktop (right column); both read
  // the same parent state so the cart stays in sync.
  const purchaseCard = (
    <Card className="sticky top-24" padding="lg">
      {ownsEverything ? (
        <button
          type="button"
          onClick={() => navigate(`/courses/${course.slug}/watch`)}
          className="w-full rounded-lg bg-orange-500 px-6 py-3 font-medium text-white hover:bg-orange-600"
        >
          {t('course:watchCourse')}
        </button>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('course:lessons')}</h2>
            {buyableLessons.length > 1 && (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                {t('course:selectAll')}
              </label>
            )}
          </div>

          <ul className="mb-5 divide-y divide-gray-100">
            {course.lessons.map((lesson, idx) => {
              const isOwned = ownedVideoIds.has(lesson.id);
              const price = method === 'wechat_pay' ? lesson.wechat : null;
              const duration = formatDuration(lesson.durationSec);
              return (
                <li key={lesson.id} className="flex items-center gap-3 py-3">
                  {isOwned ? (
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-xs text-green-600">
                      ✓
                    </span>
                  ) : (
                    <input
                      type="checkbox"
                      className="h-5 w-5 shrink-0 accent-orange-500"
                      checked={selected.has(lesson.id)}
                      onChange={() => toggleLesson(lesson.id)}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {idx + 1}. {localize(lesson.titleCN, lesson.titleEN, i18n.language)}
                    </p>
                    {duration && <p className="mt-0.5 text-xs text-gray-500">{duration}</p>}
                  </div>
                  {isOwned ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/courses/${course.slug}/watch`)}
                      className="shrink-0 text-sm font-medium text-orange-600 hover:text-orange-700"
                    >
                      {t('course:watch')}
                    </button>
                  ) : (
                    <span className="shrink-0 text-sm text-gray-700">
                      {price ? formatMoney(price.currency, price.amount) : formatUsd(lesson.price)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          {dualPricing && (
            <>
              <div className="mb-4">
                <p className="mb-2 text-sm font-medium text-gray-700">
                  {t('course:paymentMethod')}
                </p>
                <div className="space-y-2">
                  {paymentOptions.map((opt) => {
                    const active = method === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setMethod(opt.value)}
                        aria-pressed={active}
                        className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors ${
                          active
                            ? 'border-orange-500 bg-orange-50 text-gray-900'
                            : 'border-gray-200 text-gray-700 hover:border-orange-300'
                        }`}
                      >
                        <span>{opt.label}</span>
                        {active && <Check className="h-5 w-5 shrink-0 text-orange-500" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {method === 'wechat_pay' && (
                <p className="mb-4 text-xs text-gray-500">{t('course:wechatHint')}</p>
              )}
            </>
          )}

          {/* Promo code (USD checkout only). */}
          {usdContext && selectedLessons.length > 0 && (
            <div className="mb-3">
              {appliedPromo && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700">
                    {t('course:promo.discountLine', { code: appliedPromo.code })}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemovePromoCode}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    {t('course:promo.remove')}
                  </button>
                </div>
              )}
              {appliedInvite && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700">
                    {t('course:promo.inviteLine', { code: appliedInvite.code })}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveInviteCode}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    {t('course:promo.remove')}
                  </button>
                </div>
              )}
              {!(appliedPromo && appliedInvite) && (
                <div className={appliedPromo || appliedInvite ? 'mt-2' : undefined}>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder={t('course:promo.placeholder')}
                      maxLength={20}
                      className="min-w-0 flex-1 rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-orange-400 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromoCode}
                      disabled={!promoCode.trim() || promoValidating}
                      className="shrink-0 px-3 py-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 disabled:cursor-not-allowed disabled:text-gray-300"
                    >
                      {promoValidating ? '...' : t('course:promo.apply')}
                    </button>
                  </div>
                  {promoError && <p className="mt-1 text-xs text-red-500">{promoError}</p>}
                </div>
              )}
            </div>
          )}

          <div className="mb-4 space-y-1 pt-4">
            <div className="flex items-baseline justify-between text-sm text-gray-600">
              <span>{t('course:lessonsSelected', { count: selectedLessons.length })}</span>
              <span>{formatMoney(cartCurrency, selectedTotal)}</span>
            </div>
            {promoDiscount > 0 && (
              <div className="flex items-baseline justify-between text-sm text-green-700">
                <span>{t('course:promo.discountLine', { code: appliedPromo?.code ?? '' })}</span>
                <span>-{formatUsd(promoDiscount)}</span>
              </div>
            )}
            {inviteDiscount > 0 && (
              <div className="flex items-baseline justify-between text-sm text-green-700">
                <span>{t('course:promo.inviteLine', { code: appliedInvite?.code ?? '' })}</span>
                <span>-{formatUsd(inviteDiscount)}</span>
              </div>
            )}
            {creditApplied > 0 && (
              <div className="flex items-baseline justify-between text-sm text-gray-600">
                <span>{t('course:credit.applied')}</span>
                <span>-{formatUsd(creditApplied)}</span>
              </div>
            )}
            <div className="flex items-baseline justify-between pt-1">
              <span className="text-sm font-medium text-gray-700">{t('course:credit.toPay')}</span>
              <span className="text-2xl font-semibold text-gray-900">
                {formatMoney(cartCurrency, usdContext ? toPay : selectedTotal)}
              </span>
            </div>
          </div>

          <label className="mb-3 flex cursor-pointer items-start gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-orange-500"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>{t('course:agreeRefund')}</span>
          </label>

          <button
            type="button"
            onClick={handlePurchase}
            disabled={
              submitting ||
              promoValidating ||
              selectedLessons.length === 0 ||
              !course.purchasable ||
              !agreed
            }
            className="w-full rounded-lg bg-orange-500 px-6 py-3 font-medium text-white hover:bg-orange-600 disabled:opacity-60"
          >
            {!course.isLive
              ? t('course:buy.notAvailable')
              : !course.purchasable
                ? t('course:buy.temporarilyUnavailable')
                : submitting
                  ? t('course:buy.starting')
                  : t('course:buy.default')}
          </button>

          {needsLogin && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p>{t('course:loginRequired')}</p>
              <Link
                to={`/login?redirect=/courses/${slug}`}
                className="mt-1 inline-block font-medium text-orange-600 hover:underline"
              >
                {t('course:goToLogin')}
              </Link>
            </div>
          )}
        </>
      )}
      {error && <p className="mt-3 text-center text-sm text-red-600">{error}</p>}
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-custom py-4 sm:py-8">
        {!course.isLive && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <span className="font-medium">{t('course:preview.label')}</span>{' '}
            {t('course:preview.text')}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:gap-8 lg:grid-cols-3">
          {/* Left Column - Details */}
          <div className="space-y-4 sm:space-y-6 lg:col-span-2">
            <ImageGallery images={course.images} alt={title} />

            <div>
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">{title}</h1>
              <DetailActions
                className="mt-3"
                type="activity"
                targetId={course.id}
                sharePath={`/courses/${course.slug}`}
                title={title}
              />
              {(hasAgeRange || categoryLabel) && (
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-600 sm:gap-6 sm:text-sm">
                  {hasAgeRange && (
                    <div className="flex items-center gap-2">
                      <svg
                        className="h-4 w-4 sm:h-5 sm:w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                      <span>{t('course:ages', { range: ageLabel })}</span>
                    </div>
                  )}
                  {categoryLabel && (
                    <div className="flex items-center gap-2">
                      <svg
                        className="h-4 w-4 sm:h-5 sm:w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        />
                      </svg>
                      <span>{categoryLabel}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile: purchase card (shown above description on mobile) */}
            <div className="lg:hidden">{purchaseCard}</div>

            {description && (
              <Card padding="lg">
                <h2 className="mb-3 text-lg font-bold text-gray-900 sm:mb-4 sm:text-xl">
                  {t('course:about')}
                </h2>
                <div className="prose prose-sm max-w-none text-gray-700">
                  <DescriptionMarkdown lineBreaks lang={i18n.language}>
                    {description}
                  </DescriptionMarkdown>
                </div>
              </Card>
            )}

            {course.host && (
              <Card padding="lg">
                <h2 className="mb-3 text-lg font-bold text-gray-900 sm:mb-4 sm:text-xl">
                  {t('course:aboutHost')}
                </h2>
                <div className="flex items-start gap-4">
                  {course.host.avatarUrl ? (
                    <img
                      src={course.host.avatarUrl}
                      alt={course.host.displayName}
                      className="h-14 w-14 flex-shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-orange-100">
                      <span className="text-xl font-bold text-orange-600">
                        {course.host.displayName.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900">
                      {course.host.displayName}
                    </h3>
                    {hostBio && <p className="mt-1 text-sm text-gray-600">{hostBio}</p>}
                    <Link
                      to={`/activities?hostId=${course.host.id}`}
                      className="mt-2 inline-block text-sm text-orange-600 hover:underline"
                    >
                      {t('course:viewMoreByHost')}
                    </Link>
                    <FollowHostButton hostId={course.host.id} className="mt-3" />
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Purchase Card (desktop only) */}
          <div className="hidden lg:col-span-1 lg:block">{purchaseCard}</div>
        </div>
      </div>
    </div>
  );
}
